import { useState, useEffect, useCallback } from 'react'
import { LOGIN_URL, API_V1 } from '../config'
import { apiFetch, setOnUnauthorized } from '../services/api'

interface User {
  id: string
  email: string
  name: string
  avatar_url?: string
  token?: string
  refresh_token?: string
  organizations?: Array<{
    id: string
    name: string
    role: string
  }>
}

interface AuthState {
  /** Current authenticated user */
  user: User | null
  /** Whether auth state is being loaded */
  isLoading: boolean
  /** Whether the Dialog API login is in progress */
  isLoggingIn: boolean
  /** Auth error message */
  error: string | null
  /** Open Dialog API login flow */
  login: () => void
  /** Clear session and user state */
  logout: () => void
  /** Check if user is authenticated */
  isAuthenticated: boolean
}

const STORAGE_KEY = 'kobsign-word-addin-user'
const TOKEN_KEY = 'kobsign-word-addin-token'
const REFRESH_TOKEN_KEY = 'kobsign-word-addin-refresh-token'

/** Get the stored auth token, or null if not logged in */
export function getAuthToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

/** Store a new access token */
export function setAuthToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
  } catch {
    // localStorage unavailable
  }
}

/** Get the stored refresh token */
function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY)
  } catch {
    return null
  }
}

/** Clear all auth data from localStorage */
function clearAuthData() {
  localStorage.removeItem(STORAGE_KEY)
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
}

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns true if successful, false if refresh failed (user must re-login).
 */
export async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const API_BASE = API_V1.replace('/api/v1', '')
    const response = await fetch(`${API_BASE}/api/v1/office/refresh-token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (!response.ok) {
      // Refresh token expired or invalid — clear everything
      clearAuthData()
      return false
    }

    const data = await response.json()
    if (data.token) {
      setAuthToken(data.token)
    }
    if (data.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token)
    }
    return true
  } catch {
    return false
  }
}

/**
 * Auth hook using Office Dialog API for login.
 *
 * Flow:
 * 1. Opens Dialog to auth-callback.html (same origin)
 * 2. User clicks sign-in provider → OAuth in new tab
 * 3. Backend callback sends userData + token + refresh_token via BroadcastChannel
 * 4. Dialog receives data, calls messageParent(userData)
 * 5. This hook stores user info + tokens
 * 6. All API calls use Authorization: Bearer token header
 *
 * Token lifecycle:
 * - Access token (ot_): 1 hour, used for API calls
 * - Refresh token (ort_): 30 days, used to get new access tokens
 * - On mount: validates access token, refreshes if expired
 * - On 401: tries refresh before showing login prompt
 */
export function useAuth(): AuthState {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem(STORAGE_KEY)
      return cached ? JSON.parse(cached) : null
    } catch {
      return null
    }
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Register 401 handler — tries refresh before giving up
  useEffect(() => {
    setOnUnauthorized(async () => {
      const refreshed = await tryRefreshToken()
      if (!refreshed) {
        setUser(null)
        clearAuthData()
        setError('auth.sessionExpired')
      }
      // If refreshed, the retry logic in api.ts will handle it
    })
  }, [])

  // Proactive token refresh — refresh 5 min before expiry (token TTL = 1h)
  // Runs every 50 minutes to ensure token is always fresh
  useEffect(() => {
    if (!user) return
    const REFRESH_INTERVAL_MS = 50 * 60 * 1000 // 50 minutes
    const intervalId = setInterval(async () => {
      const refreshed = await tryRefreshToken()
      if (!refreshed) {
        setUser(null)
        clearAuthData()
        setError('auth.sessionExpired')
      }
    }, REFRESH_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [user])

  // Validate session on mount
  useEffect(() => {
    async function checkSession() {
      const token = getAuthToken()

      // No access token — try refresh token before showing login
      if (!token) {
        const refreshToken = getRefreshToken()
        if (refreshToken) {
          const refreshed = await tryRefreshToken()
          if (refreshed) {
            // Got a new access token — now validate it
            return await validateAccessToken()
          }
        }
        // No tokens at all — show login
        setUser(null)
        clearAuthData()
        setIsLoading(false)
        return
      }

      await validateAccessToken()
    }

    async function validateAccessToken() {
      try {
        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 8000)

        const response = await apiFetch(`${API_V1}/users/me/`, {
          signal: controller.signal,
        })
        clearTimeout(timeout)

        if (response.ok) {
          const data = await response.json()
          // /users/me/ returns { user: { ... } } — unwrap it
          const userData = data.user ?? data
          setUser(userData)
          localStorage.setItem(STORAGE_KEY, JSON.stringify(userData))
        } else if (response.status === 401) {
          // Access token expired — try refresh
          const refreshed = await tryRefreshToken()
          if (refreshed) {
            // Retry with new token
            const retryController = new AbortController()
            const retryTimeout = setTimeout(() => retryController.abort(), 8000)
            const retryResponse = await apiFetch(`${API_V1}/users/me/`, {
              signal: retryController.signal,
            })
            clearTimeout(retryTimeout)

            if (retryResponse.ok) {
              const data = await retryResponse.json()
              const retryUserData = data.user ?? data
              setUser(retryUserData)
              localStorage.setItem(STORAGE_KEY, JSON.stringify(retryUserData))
            } else {
              setUser(null)
              clearAuthData()
            }
          } else {
            setUser(null)
            clearAuthData()
          }
        } else {
          setUser(null)
          clearAuthData()
        }
      } catch (err) {
        console.warn('[KobSign] Session check failed:', err)
        setUser(null)
        clearAuthData()
      } finally {
        setIsLoading(false)
      }
    }

    checkSession()
  }, [])

  const login = useCallback(() => {
    setIsLoggingIn(true)
    setError(null)

    try {
      console.log('[KobSign] Opening dialog:', LOGIN_URL)

      Office.context.ui.displayDialogAsync(
        LOGIN_URL,
        { height: 60, width: 40, promptBeforeOpen: false },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            const msg = `Dialog error: ${result.error.code} - ${result.error.message}`
            console.error('[KobSign]', msg)
            setError(msg)
            setIsLoggingIn(false)
            return
          }

          console.log('[KobSign] Dialog opened, waiting for message...')
          const dialog = result.value

          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg) => {
              console.log('[KobSign] DialogMessageReceived:', JSON.stringify(arg).substring(0, 200))
              dialog.close()
              try {
                if ('message' in arg) {
                  const userData = JSON.parse(arg.message) as User

                  // Store tokens separately (don't persist in user object)
                  if (userData.token) {
                    setAuthToken(userData.token)
                    delete userData.token
                  }
                  if (userData.refresh_token) {
                    localStorage.setItem(REFRESH_TOKEN_KEY, userData.refresh_token)
                    delete userData.refresh_token
                  }

                  setUser(userData)
                  localStorage.setItem(STORAGE_KEY, JSON.stringify(userData))
                  setError(null)
                } else {
                  setError(`No message in arg: ${JSON.stringify(arg)}`)
                }
              } catch (e) {
                setError(`Parse error: ${e}`)
              } finally {
                setIsLoggingIn(false)
              }
            },
          )

          dialog.addEventHandler(
            Office.EventType.DialogEventReceived,
            (arg) => {
              // Dialog was closed or navigated away
              const code = 'error' in arg ? (arg as { error: number }).error : 'unknown'
              console.log('[KobSign] DialogEventReceived:', code)
              setError(`Dialog closed (event ${code}). messageParent may have failed.`)
              setIsLoggingIn(false)
            },
          )
        },
      )
    } catch (e) {
      setError(`Exception: ${e}`)
      setIsLoggingIn(false)
    }
  }, [])

  const logout = useCallback(() => {
    setUser(null)
    clearAuthData()
    // Office tokens are stateless (signed JWTs) — no backend logout needed.
    // clearAuthData() removes tokens from localStorage which is sufficient.
  }, [])

  return {
    user,
    isLoading,
    isLoggingIn,
    error,
    login,
    logout,
    isAuthenticated: user !== null,
  }
}
